import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import UploadIcon from "@/sprout/icons/upload";
import LoadingSpinner from "@/sprout/loading_spinner";
import { Body } from "@sprout/typography";
import { validateResume } from "@components/quick_apply/util";
import { useShowFlash } from "@sprout/flash";
import type { FileState } from "@/queries/use_profile";
import "@components/profile_card.scss";

type Props = {
  onUploadComplete?: () => void;
};

const ResumeUploadBox = ({ onUploadComplete }: Props) => {
  const { t } = useTranslation("user", { keyPrefix: "resume_upload_box" });
  const { t: tResume } = useTranslation("user");
  const showFlash = useShowFlash();
  const queryClient = useQueryClient();

  const [uploading, setUploading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = useCallback(
    async (file: File) => {
      const resumeUpload: FileState = {
        file,
        filename: file.name,
        url: URL.createObjectURL(file),
        content_type: file.type,
      };

      const resumeError = validateResume(resumeUpload);
      if (resumeError) {
        setValidationError(`${file.name}: ${tResume(resumeError)}`);
        return;
      }

      setValidationError(null);
      setUploading(true);

      const formData = new FormData();
      formData.append("resume", file);
      formData.append("save_profile", "true");

      const controller = new AbortController();

      try {
        await axios.post("/users/resume", formData, { signal: controller.signal });
        await queryClient.invalidateQueries({ queryKey: ["profile"] });
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }
        showFlash(tResume("resume.error_flash"), "error");
      } finally {
        setUploading(false);
      }
    },
    [showFlash, tResume, queryClient, onUploadComplete],
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) uploadFile(file);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [uploadFile],
  );

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  if (uploading) {
    return (
      <div className="flex items-center justify-center py-6">
        <LoadingSpinner size="medium" color="green" />
      </div>
    );
  }

  return (
    <div>
      <div
        className={`dropzone${dragOver ? " dropzone--drag-over" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <UploadIcon size="sm" />
        <Body>
          {t("drop_hint_before")}{" "}
          <button
            type="button"
            className="underline text-[var(--green-700)]"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            {t("browse")}
          </button>{" "}
          {t("drop_hint_after")}
        </Body>
        <Body metadata className="text-center file-types">
          {t("file_types")}
        </Body>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.txt,.rtf"
        className="hidden"
        onChange={handleFileSelect}
      />
      {validationError && (
        <p className="helper-text helper-text--error !ml-0 mt-1" aria-live="polite">
          {validationError}
        </p>
      )}
    </div>
  );
};

export default ResumeUploadBox;
